import { isIsoDate } from "./date.ts";
import {
  DEFAULT_BANDS,
  DEFAULT_DAILY_RULE,
  DomainError,
  assertImportedOutcome,
  type Category,
  type Entry,
  type IsoDate,
  type MatrixDocument,
  type Operation,
  type Outcome,
} from "./model.ts";

export interface SourceIdentity {
  readonly ownerId: string;
  readonly entity: "category" | "operation" | "entry";
  readonly sourceId: string;
}

export interface LegacyFixtureItem {
  readonly name: string;
  readonly excluded?: boolean;
}

export interface LegacyFixtureRow {
  readonly d: IsoDate;
  readonly ticks?: Readonly<Record<string, unknown>>;
}

export interface LegacyGoldenFixture {
  readonly slug: string;
  readonly category?: string;
  readonly axis?: Readonly<{ start?: IsoDate; end?: IsoDate }>;
  readonly items?: readonly LegacyFixtureItem[];
  readonly grid?: readonly LegacyFixtureRow[];
}

export interface ImportWarning {
  readonly code: "LEGACY_M_PRESERVED" | "UNKNOWN_OUTCOME_SKIPPED" | "DUPLICATE_ENTRY_LAST_WINS";
  readonly message: string;
}

export interface ImportResult {
  readonly document: MatrixDocument;
  readonly warnings: readonly ImportWarning[];
}

function cleanPart(value: string): string {
  const normalized = value.normalize("NFKC").trim().replace(/\s+/g, " ");
  if (normalized.length === 0) throw new DomainError("EMPTY_SOURCE_ID", "source ids cannot be empty");
  return encodeURIComponent(normalized);
}

export function ownerScopedSourceKey(identity: SourceIdentity): string {
  return `${cleanPart(identity.ownerId)}:${identity.entity}:${cleanPart(identity.sourceId)}`;
}

export function stableImportedId(identity: SourceIdentity): string {
  return `src:${ownerScopedSourceKey(identity)}`;
}

function fixtureOutcome(value: unknown, warnings: ImportWarning[], context: string): Outcome | null {
  if (value === undefined || value === null || String(value).trim() === "") return null;
  const normalized = String(value).trim().toUpperCase();
  try {
    assertImportedOutcome(normalized);
  } catch {
    warnings.push({ code: "UNKNOWN_OUTCOME_SKIPPED", message: `Skipped unknown outcome ${normalized} at ${context}` });
    return null;
  }
  if (normalized === "M") {
    warnings.push({ code: "LEGACY_M_PRESERVED", message: `Preserved import-only M at ${context}` });
  }
  return normalized;
}

/**
 * Maps the proven v2 golden-fixture shape without inventing new source identity.
 * Imported operations deliberately start as daily; owners configure real repeat rules later.
 */
export function adaptLegacyGoldenFixture(fixture: LegacyGoldenFixture, ownerId = "fixture-owner"): ImportResult {
  if (!fixture.slug?.trim()) throw new DomainError("INVALID_FIXTURE", "fixture.slug is required");
  const rows = fixture.grid ?? [];
  const rowDates = rows.map((row) => row.d).filter(isIsoDate).sort();
  const activeFrom = isIsoDate(fixture.axis?.start) ? fixture.axis.start : rowDates[0];
  if (activeFrom === undefined) throw new DomainError("INVALID_FIXTURE", "fixture has no valid start date");

  const categoryId = stableImportedId({ ownerId, entity: "category", sourceId: fixture.slug });
  const includedItems = (fixture.items ?? []).filter((item) => item.excluded !== true);
  const operations: Operation[] = includedItems.map((item) => {
    const sourceKey = ownerScopedSourceKey({ ownerId, entity: "operation", sourceId: `${fixture.slug}/${item.name}` });
    return {
      id: `src:${sourceKey}`,
      categoryId,
      name: item.name,
      private: true,
      sourceKey,
      activePeriods: [{ from: activeFrom, toExclusive: null }],
      ruleVersions: [{ id: `${sourceKey}:rule:0`, effectiveFrom: activeFrom, rule: DEFAULT_DAILY_RULE }],
    };
  });
  const operationByName = new Map(operations.map((operation) => [operation.name, operation]));
  const categories: Category[] = [{
    id: categoryId,
    sourceKey: ownerScopedSourceKey({ ownerId, entity: "category", sourceId: fixture.slug }),
    name: fixture.category ?? fixture.slug,
    activePeriods: [{ from: activeFrom, toExclusive: null }],
    operationOrder: operations.map((operation) => operation.id),
  }];

  const warnings: ImportWarning[] = [];
  const entryMap = new Map<string, Entry>();
  for (const row of rows) {
    if (!isIsoDate(row.d)) continue;
    for (const [name, rawOutcome] of Object.entries(row.ticks ?? {})) {
      const operation = operationByName.get(name);
      if (operation === undefined) continue;
      const outcome = fixtureOutcome(rawOutcome, warnings, `${fixture.slug}/${name}/${row.d}`);
      if (outcome === null) continue;
      const key = `${operation.id}\u0000${row.d}`;
      if (entryMap.has(key)) {
        warnings.push({ code: "DUPLICATE_ENTRY_LAST_WINS", message: `Last imported value won for ${name} on ${row.d}` });
      }
      entryMap.set(key, { operationId: operation.id, date: row.d, outcome });
    }
  }

  return {
    document: {
      ownerId,
      categories,
      operations,
      entries: [...entryMap.values()],
      bands: DEFAULT_BANDS,
      target: 85,
    },
    warnings,
  };
}
